import './styles/scrolling-tabs.css';
import './setDragImage';

import { isNonNullable } from '@ipfabric/utils';
import classNames from 'classnames';
import { pipe } from 'ramda';
import React, { PureComponent } from 'react';
import handleEvent from 'utils/handleEvent';

const SCROLL_SIZE = 150;

const getOutWidth = (node) => {
  const { width } = node.getBoundingClientRect();
  const style = getComputedStyle(node);
  return (
    width +
    Number.parseInt(style.marginLeft, 10) +
    Number.parseInt(style.marginRight, 10)
  );
};

const getInnerWidth = (node) => {
  return node.getBoundingClientRect().width;
};

// type PropsType = {
//   children: any,
//   parentSelector: string,
//   startIndex: number,
// };

export default class ScrollingTabs extends PureComponent {
  static defaultProps = {
    scWrapperClassName: '',
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.reAdjust();
    if (this.props.startIndex > 0) {
      this.scrollRight(this.itemsWidth(this.props.startIndex), false);
    }
    this.mounted = true;
    const dragIcon = new Image();
    dragIcon.src = '/public/img/one-by-one.png';
    dragIcon.onload = () => {
      if (this.mounted) {
        this.setState({ dragIcon });
      }
    };

    this.resizeObserver = new ResizeObserver(this.reAdjust);

    if (this.containerRef.current) {
      this.resizeObserver.observe(this.containerRef.current);
    }
  }

  /**
   * Component needs refactoring! componentDidUpdate() is workaround for scrolling to tab.
   * Refactor in https://ipfabric.atlassian.net/browse/NIM-6159
   */
  componentDidUpdate(prevProps) {
    this.reAdjust();
    if (
      this.props.startIndex > 0 &&
      (this.props.startIndex !== prevProps.startIndex ||
        this.props.activeTabId !== prevProps.activeTabId)
    ) {
      this.scrollRight(this.itemsWidth(this.props.startIndex), false);
    }
    /*
     * Scroll to start with startIndex === 0.
     * However, when new tab is prepended and current startIndex === 0, scroll isn't activated.
     * Scroll is triggered on activeTabId change.
     */
    if (
      this.props.startIndex === 0 &&
      (this.props.startIndex !== prevProps.startIndex ||
        this.props.activeTabId !== prevProps.activeTabId)
    ) {
      this.scrollStart();
    }
    if (this.props.endIndex > 0 && this.props.endIndex !== prevProps.endIndex) {
      this.scrollEnd();
    }

    if (
      this.props.itemsCount !== undefined &&
      this.props.endIndex !== undefined &&
      this.props.itemsCount !== prevProps.itemsCount
    ) {
      this.scrollToItem(this.props.endIndex);
    }
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.frameId);
    this.mounted = false;

    if (this.containerRef.current) {
      this.resizeObserver.unobserve(this.containerRef.current);
    }
  }

  getLeftPosition = () => {
    const container = this.getContainer();
    if (isNonNullable(this.leftPosition)) {
      return this.leftPosition;
    }
    const list = container.querySelector('.list');
    const style = getComputedStyle(list);
    return Number.parseInt(style.marginLeft, 10);
  };

  handleDragStart = handleEvent((event) => {
    const listLeftPosition = this.getLeftPosition();
    const widthOfHidden = this.widthOfHidden();
    if (widthOfHidden < 0 || listLeftPosition < 0) {
      this.isDragging = true;
    }
    this.dragclientX = event.clientX;
    event.dataTransfer.setDragImage(this.state.dragIcon, 0, 0);
  });

  handleDrag = handleEvent((event) => {
    if (this.isDragging) {
      const change =
        this.dragclientX && event.clientX
          ? event.clientX - this.dragclientX
          : 0;
      if (change < 0) {
        this.frameId = requestAnimationFrame(() => {
          this.scrollRight(Math.abs(change), false);
        });
      } else if (change > 0) {
        this.frameId = requestAnimationFrame(() => {
          this.scrollLeft(change, false);
        });
      }
      this.dragclientX = event.clientX;
    }
  });

  handleDragEnd = handleEvent(() => {
    this.isDragging = false;
    this.dragclientX = null;
  });

  handleLeftClick = handleEvent(() => {
    this.scrollLeft(SCROLL_SIZE, true);
  });

  handleRightClick = handleEvent(() => {
    this.scrollRight(SCROLL_SIZE, true);
  });

  getContainer = () => {
    const { container, parentSelector } = this.props;
    if (container) {
      return container;
    }
    return document.querySelector(parentSelector);
  };

  widthOfHidden = () => {
    const container = this.getContainer();
    const itemsWidth = this.itemsWidth();
    const scrollBarWidths = 48;
    const wrapper = container.querySelector('.sc-wrapper');
    const wrapperWidth = getOutWidth(wrapper);

    return wrapperWidth - itemsWidth - this.getLeftPosition() - scrollBarWidths;
  };

  scrollStart = () => {
    /**
     * Component needs to be refactored, completely.
     * 'leftPosition' is negative but 'scrollLeft()' accepts positive number.
     * To scroll to the left, to start, we must send current offset (leftPosition)
     * but convert it to positive number;
     */
    if (this.leftPosition !== 0) {
      this.scrollLeft(Math.abs(this.getLeftPosition()), false);
    }
  };

  scrollEnd = () => {
    const container = this.getContainer();
    const wrapperWidth = getInnerWidth(container.querySelector('.sc-wrapper'));
    const itemsWidth = this.itemsWidth(this.props.endIndex + 1); // add 1 because of slice
    const leftPosition = this.leftPosition || 0;
    const difference = itemsWidth + leftPosition - wrapperWidth;
    if (difference > 0) {
      this.scrollRight(difference, false);
    }
  };

  /**
   * Scroll to tab based on index. Unclear code.
   * Will be refactored in https://ipfabric.atlassian.net/browse/NIM-6159
   */
  scrollToItem = (tabIndex) => {
    const container = this.getContainer();
    const wrapperWidth = getInnerWidth(container.querySelector('.sc-wrapper'));
    const tabsWidth = this.itemsWidth(tabIndex + 1); // add 1 because of slice inside itemsWidth()
    const leftPosition = this.leftPosition || 0;
    const difference = tabsWidth + leftPosition - wrapperWidth;
    if (difference > 0) {
      this.scrollRight(difference, false);
    } else {
      this.scrollLeft(Math.abs(difference), false);
    }
  };

  scrollLeft = (scrollSize, animation = true) => {
    const container = this.getContainer();
    container.querySelector('.scroller-right').classList.remove('disabled');
    const listLeftPosition = this.getLeftPosition();
    if (listLeftPosition === 0) {
      container.querySelector('.scroller-left').classList.add('disabled');
      return;
    }
    const positionChange =
      listLeftPosition + scrollSize >= 0
        ? Math.abs(listLeftPosition)
        : scrollSize;
    this.leftPosition = (this.leftPosition || 0) + positionChange;
    const list = container.querySelector('.list');
    if (animation) {
      if (!list.classList.contains('animate-left')) {
        list.classList.add('animate-left');
      }
      list.style.left = `${this.leftPosition}px`;
    } else {
      if (list.classList.contains('animate-left')) {
        list.classList.remove('animate-left');
      }
      list.style.left = `${this.leftPosition}px`;
    }
    if (this.leftPosition >= 0) {
      container.querySelector('.scroller-left').classList.add('disabled');
    }
  };

  scrollRight = (scrollSize, animation = true) => {
    const container = this.getContainer();
    container.querySelector('.scroller-left').classList.remove('disabled');
    const hiddenWidth = this.widthOfHidden();
    const positionChange =
      Math.abs(hiddenWidth) - scrollSize <= 0
        ? Math.abs(hiddenWidth)
        : scrollSize;
    const wrapper = container.querySelector('.sc-wrapper');
    const wrapperWidth = getOutWidth(wrapper);
    const itemsWidth = this.itemsWidth();
    if (itemsWidth < wrapperWidth) {
      return;
    }
    const maxLeftPosition = -itemsWidth + wrapperWidth;
    this.leftPosition = Math.max(
      (this.leftPosition || 0) - positionChange,
      maxLeftPosition,
    );
    const list = container.querySelector('.list');
    if (animation) {
      if (!list.classList.contains('animate-left')) {
        list.classList.add('animate-left');
      }
      list.style.left = `${this.leftPosition}px`;
    } else {
      if (list.classList.contains('animate-left')) {
        list.classList.remove('animate-left');
      }
      list.style.left = `${this.leftPosition}px`;
    }
    if (Math.abs(hiddenWidth) - scrollSize < 0) {
      container.querySelector('.scroller-right').classList.add('disabled');
    }
  };

  itemsWidth = (startIndex) => {
    const container = this.getContainer();
    const itemsWidth = pipe(
      (list) => {
        const { childNodes } = list;
        return childNodes?.length ? [...childNodes] : [];
      },
      (list) => {
        return list.slice(0, startIndex || list.length).reduce((acc, node) => {
          const width = getOutWidth(node);
          return acc + width;
        }, 0);
      },
    )(container.querySelector('.list'));
    return itemsWidth;
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
  reAdjust = () => {
    const container = this.getContainer();
    const itemsWidth = this.itemsWidth();

    const leftPosition = this.getLeftPosition();
    if (isNonNullable(this.outerWidth)) {
      const outerWidth = getOutWidth(container.querySelector('.sc-wrapper'));
      if (leftPosition < 0) {
        if (outerWidth > this.outerWidth) {
          const newPosition = leftPosition + (outerWidth - this.outerWidth);
          const newValue = newPosition > 0 ? 0 : newPosition;
          this.leftPosition = newValue;
          container.querySelector('.list').style.left = `${newValue}px`;
        }
      }
      this.outerWidth = outerWidth;
    } else {
      this.outerWidth = getOutWidth(container.querySelector('.sc-wrapper'));
    }

    if (getOutWidth(container.querySelector('.sc-wrapper')) < itemsWidth) {
      container.querySelector('.scroller-left').style.display = 'block';
      container.querySelector('.scroller-right').style.display = 'block';
      if (this.getLeftPosition() === 0) {
        container.querySelector('.scroller-left').classList.add('disabled');
      }
    } else {
      container.querySelector('.scroller-left').style.display = 'none';
      container.querySelector('.scroller-right').style.display = 'none';
    }
    const widthOfHidden = this.widthOfHidden();
    if (Math.abs(widthOfHidden) > 0) {
      container.querySelector('.scroller-right').classList.remove('disabled');
    }

    if (this.getLeftPosition() < 0) {
      container.querySelector('.scroller-left').style.display = 'block';
    }
  };

  render() {
    const { scContainerClassName, scWrapperClassName } = this.props;

    return (
      <div
        className={classNames('sc-container', scContainerClassName)}
        ref={this.containerRef}
      >
        <div
          className='scroller scroller-left disabled'
          onClick={this.handleLeftClick}
        >
          <i className='fa fa-chevron-left' />
        </div>
        <div
          className='scroller scroller-right'
          onClick={this.handleRightClick}
        >
          <i className='fa fa-chevron-right' />
        </div>
        <div className={classNames('sc-wrapper', scWrapperClassName)}>
          <div
            className='nav nav-tabs list'
            onDragStart={this.handleDragStart}
            onDragEnd={this.handleDragEnd}
            onDrag={this.handleDrag}
          >
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}
